import { AppLocaleSelector } from "@/components/elements/locale-selector";
import { getProductInfo } from "@/components/layouts/products";
import { useSidebarExpanded } from "@/components/layouts/use-sidebar-expanded";
import { AppSidebarHeader } from "@/components/sidebar/app-sidebar-header";
import { SidebarModeToggle } from "@/components/sidebar/sidebar-theme-toggle";
import { env } from "@/config/environment-vars";
import { useAuth } from "@/contexts/auth";
import {
  BaseSidebar,
  BaseSidebarFooter,
  BaseSidebarFooterAction,
  BaseSidebarNav,
  type BaseSidebarNavItemConfig,
  baseSidebarNavButtonClassName,
  cn,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@cmde/shadcn";
import { LogOutIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

export const AppSidebar = () => {
  const { products } = useAuth();
  const { isExpanded } = useSidebarExpanded();
  const currentProduct = products?.find((p) => p.code === env.VITE_PROJECT_CODE);
  const productsWithoutCurrent = products?.filter((p) => p.code !== env.VITE_PROJECT_CODE) ?? [];
  const productsToShow = currentProduct ? [currentProduct, ...productsWithoutCurrent] : products;

  const navItems =
    productsToShow?.map((product) => {
      const productInfo = getProductInfo(product.code);
      return {
        key: product.code,
        icon: productInfo.icon,
        label: product.name,
        active: currentProduct?.code === product.code,
      };
    }) ?? [];

  return (
    <BaseSidebar
      className="p-0 dark:border-white/15!"
      headerClassName={cn("p-0", "max-md:border-b-0!")}
      header={<AppSidebarHeader />}
      mobileSlot={<AppLocaleSelector inMobileSidebar />}
      mobileSlotClassName="px-4 py-3"
      footer={<AppSidebarFooter />}
      showToggleButton={!isExpanded}
      overlay={true}
    >
      <BaseSidebarNav
        items={navItems as BaseSidebarNavItemConfig[]}
        renderItem={(item) => {
          const link = getProductInfo(item.key).link || "";
          const isExternalLink = link.startsWith("http");
          const Icon = item.icon;

          return (
            <SidebarMenuItem key={item.key} className="w-full">
              <SidebarMenuButton
                asChild
                variant="nav"
                tooltip={item.label}
                isActive={item.active}
                data-sidebar-nav="item"
                className={baseSidebarNavButtonClassName}
              >
                <Link
                  to={link}
                  target={isExternalLink ? "_blank" : undefined}
                  rel={isExternalLink ? "noopener noreferrer" : undefined}
                >
                  <Icon />
                  <span className="truncate">{item.label}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        }}
      />
    </BaseSidebar>
  );
};

const AppSidebarFooter = () => {
  const { signOut } = useAuth();
  const { t } = useTranslation();

  return (
    <BaseSidebarFooter className="pb-[env(safe-area-inset-bottom)]">
      <SidebarModeToggle />
      <BaseSidebarFooterAction
        icon={LogOutIcon as BaseSidebarNavItemConfig["icon"]}
        label={t("components.sidebar.general.log_out")}
        className="cursor-pointer"
        onClick={signOut}
      />
    </BaseSidebarFooter>
  );
};
